import { Instance } from 'mobx-state-tree';
import React, { useCallback, useMemo, useState } from 'react';
import { Anchor, Group, Pharmacy } from '../../store/models';
import { toJS } from 'mobx';
import { Form } from 'react-final-form';
import LeaveRouteDirtyWrapper from '../common/LeaveRouteDirtyWrapper';
import styled from '@emotion/styled';
import VerticalForm from '../common/form/VerticalForm';
import { GroupFormField, TextInputFormField } from '../common/form';
import { TLCode } from '../../common/enums';
import { Button, FormGroup, Intent, MenuItem } from '@blueprintjs/core';
import { WithTranslateProps } from '../common/WithTranslate';
import { IItemRendererProps, MultiSelect } from '@blueprintjs/select';
import { IconNames } from '@blueprintjs/icons';
import { ChromePicker } from 'react-color';

interface AnchorFormProps extends WithTranslateProps {
  anchor?: Instance<typeof Anchor>;
  save: (data: Instance<typeof Anchor>) => void;
  groups: Array<Instance<typeof Group>>;
  pharmacies: Array<Instance<typeof Pharmacy>>;
  linkPharmacyToAnchor: (pharmacyId: number, anchorId: number) => void;
  unlinkPharmacyFromAnchor: (pharmacyId: number) => void;
}

const Container = styled.div`
  display: flex;
  width: 70%;
  flex-direction: column;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-content: center;
  justify-content: flex-end;
  & button {
    margin-left: 5px;
  }
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const AnchorForm = ({
  anchor,
  save,
  groups,
  pharmacies,
  translate,
  linkPharmacyToAnchor,
  unlinkPharmacyFromAnchor,
}: AnchorFormProps) => {
  const [selectedPharmacies, setSelectedPharmacies] = useState<Array<Instance<typeof Pharmacy>>>(
    pharmacies.filter((p) => anchor !== undefined && p.anchorId === anchor.anchorId),
  );
  const [pharmaciesToLink, setPharmaciesToLink] = useState<Array<Instance<typeof Pharmacy>>>([]);
  const [pharmaciesToUnlink, setPharmaciesToUnlink] = useState<Array<Instance<typeof Pharmacy>>>([]);
  const [color, setColor] = useState<string>(
    anchor === undefined || anchor!.color === undefined ? '000000' : anchor!.color,
  );

  const pharmacyItemDisplayed = useMemo(() => (p: Instance<typeof Pharmacy>) => `${p.apbNumber} - ${p.name}`, []);

  const onPharmacySelected = useCallback(
    (item: Instance<typeof Pharmacy>) => {
      setSelectedPharmacies([...selectedPharmacies, item]);
      setPharmaciesToLink([...pharmaciesToLink, item]);
      setPharmaciesToUnlink(pharmaciesToUnlink.filter((p) => p.pharmacyId !== item.pharmacyId));
    },
    [
      selectedPharmacies,
      pharmaciesToLink,
      setPharmaciesToLink,
      setSelectedPharmacies,
      pharmaciesToUnlink,
      setPharmaciesToUnlink,
    ],
  );

  const onPharmacyRemoved = useCallback(
    (value: string, index: number) => {
      setSelectedPharmacies(selectedPharmacies.filter((p) => pharmacyItemDisplayed(p) !== value));
      setPharmaciesToLink(pharmaciesToLink.filter((p) => pharmacyItemDisplayed(p) !== value));
      setPharmaciesToUnlink([...pharmaciesToUnlink, pharmacies.find((p) => pharmacyItemDisplayed(p) === value)!]);
    },
    [
      selectedPharmacies,
      pharmaciesToLink,
      setPharmaciesToLink,
      setSelectedPharmacies,
      pharmaciesToUnlink,
      setPharmaciesToUnlink,
      pharmacyItemDisplayed,
    ],
  );

  const pharmacyItemRenderer = useCallback(
    (item: Instance<typeof Pharmacy>, { handleClick, modifiers }: IItemRendererProps) => {
      if (!modifiers.matchesPredicate) return null;
      return (
        <MenuItem
          active={modifiers.active}
          key={item.pharmacyId}
          onClick={handleClick}
          text={pharmacyItemDisplayed(item)}
        />
      );
    },
    [pharmacyItemDisplayed],
  );

  return (
    <Form
      initialValues={toJS(anchor) || {}}
      onSubmit={(data) => {
        const anchorToSave = data as Instance<typeof Anchor>;
        save({ ...anchorToSave, color });
        pharmaciesToLink.forEach((p) => linkPharmacyToAnchor(p.pharmacyId, anchorToSave.anchorId));
        pharmaciesToUnlink.forEach((p) => unlinkPharmacyFromAnchor(p.pharmacyId));
      }}
      render={({ handleSubmit, form, hasValidationErrors, dirty, values }) => {
        return (
          <form onSubmit={handleSubmit}>
            <LeaveRouteDirtyWrapper blockChange={dirty} />
            <Container>
              <VerticalForm labelWidth={175}>
                <TextInputFormField name="name" label={TLCode.ANCHOR_Name} inline={true} fill={true} required={true} />
                <GroupFormField
                  label={TLCode.PHARMACIES_Group}
                  name="groupId"
                  groupList={groups}
                  isClearable={true}
                  required={true}
                />
                <ChromePicker
                  color={color}
                  onChange={(e) => {
                    setColor(e.hex.replace('#', ''));
                  }}
                />
                {anchor === undefined ? (
                  <></>
                ) : (
                  <FormGroup label={translate(TLCode.PHARMACIES)} style={{ marginTop: '20px' }} inline={true}>
                    <>
                      <MultiSelect
                        itemRenderer={pharmacyItemRenderer}
                        items={pharmacies.filter((r) => values.groupId === undefined || r.groupId === values.groupId)}
                        onItemSelect={onPharmacySelected}
                        tagRenderer={(item: Instance<typeof Pharmacy>) => pharmacyItemDisplayed(item)}
                        itemPredicate={(query, item) => {
                          return pharmacyItemDisplayed(item)
                            .toLocaleLowerCase()
                            .includes(query.toLocaleLowerCase());
                        }}
                        tagInputProps={{
                          onRemove: onPharmacyRemoved,
                        }}
                        selectedItems={selectedPharmacies}
                        resetOnSelect
                        placeholder={translate(TLCode.ANCHOR_SelectPharmacies)}
                      />
                    </>
                  </FormGroup>
                )}
              </VerticalForm>
              <ButtonsContainer>
                <Button
                  text={translate(TLCode.GENERAL_Cancel)}
                  icon={IconNames.ERASER}
                  intent={Intent.PRIMARY}
                  onClick={() => form.reset()}
                  disabled={!dirty}
                />
                <Button
                  type={'submit'}
                  text={translate(TLCode.GENERAL_Save)}
                  icon={IconNames.FLOPPY_DISK}
                  intent={Intent.PRIMARY}
                  disabled={hasValidationErrors}
                />
              </ButtonsContainer>
            </Container>
          </form>
        );
      }}
    />
  );
};
